import type { User, AuthTokens, LoginCredentials, SignupCredentials } from '../types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

interface TokenResponseWithUser extends AuthTokens {
  user: User;
}

class AuthAPI {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE}/api/v1/auth${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || `Error: ${response.status}`);
    }

    return response.json();
  }

  async signup(credentials: SignupCredentials): Promise<TokenResponseWithUser> {
    return this.request('/signup', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async login(credentials: LoginCredentials): Promise<TokenResponseWithUser> {
    return this.request('/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async logout(accessToken: string): Promise<void> {
    await this.request('/logout', {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  }

  async refreshToken(refreshToken: string): Promise<TokenResponseWithUser> {
    return this.request('/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  }

  async getMe(accessToken: string): Promise<User> {
    return this.request('/me', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  }
}

export const authApi = new AuthAPI();
